"use client";

/**
 * The landing's replay side of the ONE post-login intent carrier
 * (`lib/postLoginIntent`): once auth is ready, the parked intent — deploy an
 * existing team, build a new one, or resume a plan checkout — is consumed
 * exactly once and replayed through the landing's handlers, which route the
 * modal intents into the canonical team-modal controller
 * (`openExisting`/`openNew`).
 *
 * Reopening a TEAM's view is deliberately not carried here. It is already
 * served by the `?viewTeam=` URL grammar riding the auth return route
 * (`lib/authReturnRoute` + `useViewTeamUrlRestore`); a second reopen path
 * would race that one and resurrect a stale open on some later visit. Do not
 * add one back.
 */

import { useEffect } from "react";
import {
  MobileModalDebugBranch,
  MobileModalDebugEvent,
  type PersistentDebugLogger,
  captureMobileModalEnvironment,
} from "@/hooks/telemetry";
import { TeamModalEditIntent } from "@/contexts/team-modal";
import type { TeamCrafterOpenRequest } from "@/contexts/team-crafter";
import { PostLoginIntentKind } from "@/domain/post-login-intent/enums";
import type {
  PostLoginIntent,
  PostLoginTeamCrafterIntent,
} from "@/domain/post-login-intent/types";
import {
  claimPostLoginIntent,
  readPostLoginIntentRequest,
} from "@/lib/postLoginIntent";
import { TeamCrafterMode } from "@cabal/shared/domain/team-crafter/enums.js";
import type { OpenTeamEditorInput } from "./domain/home-page/types";

interface StartPlanCheckoutFlowInput {
  paddlePriceId: string;
}

interface PostLoginIntentRestoreInputLocal {
  authLoading: boolean;
  isAuthReady: boolean;
  modalDebugLogger: PersistentDebugLogger;
  /** Deploy intents resume into the canonical team modal (deploy invoked). */
  openDeployForTeam: (teamId: string) => void;
  /** Build intents resume into the canonical team modal's empty subject. */
  openNewTeamEditor: () => void;
  /** Crafter intents in EDIT mode reopen the team through its own editor
   *  grammar (the `?viewTeam&mode=copy` dispatcher) before the Crafter opens. */
  openTeamEditor: (input: OpenTeamEditorInput) => void;
  /** Crafter intents open the ONE Team Crafter overlay (post-auth: no gate). */
  openTeamCrafter: (request: TeamCrafterOpenRequest) => void;
  startPlanCheckoutFlow: (input: StartPlanCheckoutFlowInput) => void;
  userId: string | null;
}

function getPostLoginIntentBranch(input: {
  isAuthReady: boolean;
  intent: PostLoginIntent | null;
}): string {
  if (!input.isAuthReady) {
    return MobileModalDebugBranch.AuthNotReady;
  }

  return input.intent?.kind ?? MobileModalDebugBranch.None;
}

function logPostLoginIntentCheck(input: {
  intent: PostLoginIntent | null;
  isAuthReady: boolean;
  loading: boolean;
  modalDebugLogger: PersistentDebugLogger;
  userId: string | null;
}): void {
  input.modalDebugLogger.logBranchChoice({
    name: MobileModalDebugEvent.PostLoginIntentCheck,
    selectedBranch: getPostLoginIntentBranch({
      isAuthReady: input.isAuthReady,
      intent: input.intent,
    }),
    payload: {
      userId: input.userId,
      loading: input.loading,
      environment: captureMobileModalEnvironment(),
    },
  });
}

interface BuildPostLoginRestoreActionInput {
  intent: PostLoginIntent;
  openDeployForTeam: (teamId: string) => void;
  openNewTeamEditor: () => void;
  openTeamEditor: (input: OpenTeamEditorInput) => void;
  openTeamCrafter: (request: TeamCrafterOpenRequest) => void;
  startPlanCheckoutFlow: (input: StartPlanCheckoutFlowInput) => void;
  modalDebugLogger: PersistentDebugLogger;
}

/**
 * A crafter park replays as its entrance would have run signed in. Create
 * opens the Crafter on an empty draft. Edit reopens the team's edit surface
 * first — a signed-out edit is always a public team's in-memory copy, so the
 * copy intent is the one the editor grammar admits — and then opens the
 * Crafter on that team; the editor lends its draft to the Crafter as soon as
 * it enters edit. A team-less edit park (a New subject cannot be parked, it is
 * itself sign-in gated) falls back to the create open.
 */
function buildTeamCrafterRestoreAction({
  intent,
  openTeamEditor,
  openTeamCrafter,
  modalDebugLogger,
}: {
  intent: PostLoginTeamCrafterIntent;
  openTeamEditor: (input: OpenTeamEditorInput) => void;
  openTeamCrafter: (request: TeamCrafterOpenRequest) => void;
  modalDebugLogger: PersistentDebugLogger;
}): () => void {
  const teamId = intent.teamId ?? null;
  modalDebugLogger.logEventFlow({
    name: MobileModalDebugEvent.PostLoginOpenTeamCrafter,
    payload: {
      mode: intent.mode,
      teamId,
      environment: captureMobileModalEnvironment(),
    },
  });
  if (intent.mode === TeamCrafterMode.Edit && teamId != null) {
    return () => {
      openTeamEditor({ teamId, editIntent: TeamModalEditIntent.Copy });
      openTeamCrafter({ mode: TeamCrafterMode.Edit, teamId });
    };
  }
  return () => openTeamCrafter({ mode: TeamCrafterMode.Create });
}

/** The replay for one parked intent — narrowed off the carrier's
 *  discriminated union, so every kind names its exact payload. */
function buildPostLoginRestoreAction({
  intent,
  openDeployForTeam,
  openNewTeamEditor,
  openTeamEditor,
  openTeamCrafter,
  startPlanCheckoutFlow,
  modalDebugLogger,
}: BuildPostLoginRestoreActionInput): () => void {
  if (intent.kind === PostLoginIntentKind.TeamModalDeploy) {
    const { teamId } = intent;
    modalDebugLogger.logEventFlow({
      name: MobileModalDebugEvent.PostLoginPreflightTeamRestored,
      payload: {
        teamId,
        environment: captureMobileModalEnvironment(),
      },
    });
    return () => openDeployForTeam(teamId);
  }
  if (intent.kind === PostLoginIntentKind.TeamModalNewTeam) {
    modalDebugLogger.logEventFlow({
      name: MobileModalDebugEvent.PostLoginOpenNewTeam,
      payload: {
        environment: captureMobileModalEnvironment(),
      },
    });
    return () => openNewTeamEditor();
  }
  if (intent.kind === PostLoginIntentKind.TeamCrafter) {
    return buildTeamCrafterRestoreAction({
      intent,
      openTeamEditor,
      openTeamCrafter,
      modalDebugLogger,
    });
  }
  const { paddlePriceId } = intent;
  return () => startPlanCheckoutFlow({ paddlePriceId });
}

export function usePostLoginIntentRestore(
  input: PostLoginIntentRestoreInputLocal,
): void {
  const {
    authLoading,
    isAuthReady,
    modalDebugLogger,
    openDeployForTeam,
    openNewTeamEditor,
    openTeamEditor,
    openTeamCrafter,
    startPlanCheckoutFlow,
    userId,
  } = input;

  useEffect(() => {
    let cancelled = false;

    const request = isAuthReady ? readPostLoginIntentRequest() : null;
    logPostLoginIntentCheck({
      intent: request?.intent ?? null,
      isAuthReady,
      loading: authLoading,
      modalDebugLogger,
      userId,
    });

    if (!isAuthReady || request == null) {
      return undefined;
    }

    // Consumed exactly once, and across tabs wherever the platform has a lock
    // manager: the carrier decides the take on the `navigator.locks` lock that
    // names the request, so a second auth-ready landing racing this one is told
    // it lost before anything reaches the screen and replays nothing. Without a
    // manager (an insecure origin, some embedded webviews) the carrier falls
    // back to a guarded removal, which is exactly-once within this document and
    // best-effort across documents — false still means "do not act", it is the
    // cross-tab uniqueness that is not guaranteed there. The answer arrives on
    // a later task, which also keeps the replay out of this effect's own commit
    // — the deferral the queued microtask used to provide. A re-run of this
    // effect is handed the answer this document already got rather than asking
    // again, and only the run that is still live replays it.
    void claimPostLoginIntent(request.requestId).then((claimed: boolean): void => {
      if (!claimed || cancelled) return;
      const restore = buildPostLoginRestoreAction({
        intent: request.intent,
        openDeployForTeam,
        openNewTeamEditor,
        openTeamEditor,
        openTeamCrafter,
        startPlanCheckoutFlow,
        modalDebugLogger,
      });
      restore();
    });

    return (): void => {
      cancelled = true;
    };
  }, [
    authLoading,
    isAuthReady,
    modalDebugLogger,
    openDeployForTeam,
    openNewTeamEditor,
    openTeamCrafter,
    openTeamEditor,
    startPlanCheckoutFlow,
    userId,
  ]);
}
